import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import get from "lodash/get";
import { ReactSortable } from "react-sortablejs";
import "./style.scss";
import Const from "../../utils/constant";
import { uploadImage } from "../../api/image";

const DragDropImage = ({ setFieldValue, fieldName, values, isDragAndDrop }) => {
  const [files, setFiles] = useState([]);
  const isUpdatingRef = useRef(false);
  const lastFilesRef = useRef([]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (isUpdatingRef.current) return;

      isUpdatingRef.current = true;

      const filePreviews = await Promise.all(
        acceptedFiles.map(async (file, index) => {
          const res = await uploadImage({ file });
          return {
            file,
            preview: res?.data?.file_url,
            sequence_number: files.length + index + 1,
          };
        })
      );

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, ...filePreviews];
        setFieldValue(fieldName, updatedFiles);
        lastFilesRef.current = updatedFiles;
        return updatedFiles;
      });

      isUpdatingRef.current = false;
    },
    [setFieldValue, fieldName, files]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      "image/gif": [".gif"],
      "video/mp4": [".mp4"],
      "video/avi": [".avi"],
      "video/webm": [".webm"],
    },
  });

  const removeFile = (file) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((f) => f.preview !== file.preview);

      if (updatedFiles.length !== prevFiles.length) {
        setFieldValue(fieldName, updatedFiles);
      }

      lastFilesRef.current = updatedFiles;
      isUpdatingRef.current = false;
      return updatedFiles;
    });
  };

  useEffect(() => {
    const existingImages = get(values, fieldName, []);
    const validImages = existingImages.filter((img) => {
      const link =
        img?.preview ||
        img?.multi_image_link ||
        img?.image_link ||
        img?.portfolio_image_link;
      return !!link;
    });

    const existingFiles = validImages.map((url, index) => ({
      name: `existing-image-${index}`,
      preview:
        url.preview ||
        url.multi_image_link ||
        url.image_link ||
        url.portfolio_image_link,
      existing: true,
      id: url.id,
      sequence_number: url.sequence_number || index + 1,
    }));

    if (
      JSON.stringify(existingFiles) !== JSON.stringify(lastFilesRef.current)
    ) {
      setFiles(existingFiles);
      lastFilesRef.current = existingFiles;
    }
  }, [values, fieldName]);

  const handleDelete = (file, event) => {
    event.stopPropagation();
    removeFile(file);
  };

  const onSort = (order) => {
    const sortedFiles = order.map((preview) =>
      files.find((file) => file.preview === preview)
    );
    setFiles(sortedFiles);
    setFieldValue(fieldName, sortedFiles);
  };

  return (
    <div
      {...getRootProps({
        className:
          "border-2 border-dashed border-gray-300 p-4 rounded-md cursor-pointer",
      })}>
      <input {...getInputProps()} className="hidden" />

      {files.length > 0 && isDragAndDrop && (
        <ReactSortable
          list={files}
          setList={(newState) => {
            if (JSON.stringify(newState) !== JSON.stringify(files)) {
              setFiles(newState);
              setFieldValue(fieldName, newState);
            }
          }}
          className="flex gap-4 overflow-x-auto mt-4">
          {files.map((file) => (
            <div
              key={file?.preview}
              className="relative flex-shrink-0 w-40 h-40 border rounded-md overflow-hidden">
              {!file?.preview?.endsWith("mp4") ? (
                <img
                  src={Const.getS3File(file.preview)}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={Const.getS3File(file.preview)}
                  controls
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={(event) => handleDelete(file, event)}
                className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600">
                Remove
              </button>
            </div>
          ))}
        </ReactSortable>
      )}

      {files.length > 0 && !isDragAndDrop && (
        <div className="flex gap-4 mt-4 overflow-x-auto">
          {files.map((file) => (
            <div
              key={file?.preview}
              className="relative flex-shrink-0 w-40 h-40 border rounded-md overflow-hidden">
              {!file?.preview?.endsWith("mp4") ? (
                <img
                  src={Const.getS3File(file.preview)}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={Const.getS3File(file.preview)}
                  controls
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={(event) => handleDelete(file, event)}
                className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600">
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DragDropImage;
